import { useState } from 'react';
import {
  useModelsPaginated,
  useCreateModel,
  useUpdateModel,
  useDeleteModel,
  useModels,
} from '@/hooks';
import {
  useVersionsPaginated,
  useCreateVersion,
  useUpdateVersion,
  useDeleteVersion,
} from '@/hooks';
import { usePagination } from '@/hooks';
import type { ModelWithVersions, Version } from '@geely/shared';
import type { ModelFormData, VersionFormData } from '@geely/shared/schemas';
import { toast } from 'sonner';

export function useModelsPageData() {
  const { page: modelsPage, limit: modelsLimit, setPage: setModelsPage } = usePagination();
  const { data: paginatedModels, isLoading: isLoadingModels } = useModelsPaginated({ page: modelsPage, limit: modelsLimit });
  const models = paginatedModels?.data ?? [];
  const modelsPaginationMeta = paginatedModels?.meta;

  const [isVersionDialogOpen, setIsVersionDialogOpen] = useState(false);
  const [editingVersion, setEditingVersion] = useState<Version | null>(null);

  const { data: allModels = [] } = useModels({ enabled: isVersionDialogOpen });

  // Version filtering by model
  const [selectedModelFilter, setSelectedModelFilter] = useState<string | undefined>(undefined);
  const { page: versionsPage, limit: versionsLimit, setPage: setVersionsPage, resetPage: resetVersionsPage } = usePagination();
  const { data: paginatedVersions, isLoading: isLoadingVersions } = useVersionsPaginated({
    modelId: selectedModelFilter,
    page: versionsPage,
    limit: versionsLimit,
  });
  const versions = paginatedVersions?.data ?? [];
  const versionsPaginationMeta = paginatedVersions?.meta;

  // Model mutations
  const createModelMutation = useCreateModel();
  const updateModelMutation = useUpdateModel();
  const deleteModelMutation = useDeleteModel();

  // Version mutations
  const createVersionMutation = useCreateVersion();
  const updateVersionMutation = useUpdateVersion();
  const deleteVersionMutation = useDeleteVersion();

  // Model dialog state
  const [isModelDialogOpen, setIsModelDialogOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<ModelWithVersions | null>(null);

  // Model handlers
  const handleOpenModelDialog = (model?: ModelWithVersions) => {
    setEditingModel(model || null);
    setIsModelDialogOpen(true);
  };

  const handleCloseModelDialog = () => {
    setIsModelDialogOpen(false);
    setEditingModel(null);
  };

  const handleSubmitModel = async (data: ModelFormData) => {
    try {
      if (editingModel) {
        await updateModelMutation.mutateAsync({
          id: editingModel.id,
          data,
        });
        toast.success('Modelo atualizado com sucesso!');
      } else {
        await createModelMutation.mutateAsync(data);
        toast.success('Modelo criado com sucesso!');
      }
      handleCloseModelDialog();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Erro ao salvar modelo');
    }
  };

  const handleDeleteModel = async (id: string) => {
    try {
      await deleteModelMutation.mutateAsync(id);
      toast.success('Modelo excluído com sucesso!');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Erro ao excluir modelo');
    }
  };

  // Version handlers
  const handleOpenVersionDialog = (version?: Version) => {
    setEditingVersion(version || null);
    setIsVersionDialogOpen(true);
  };

  const handleCloseVersionDialog = () => {
    setIsVersionDialogOpen(false);
    setEditingVersion(null);
  };

  const handleSubmitVersion = async (data: VersionFormData) => {
    try {
      if (editingVersion) {
        await updateVersionMutation.mutateAsync({
          id: editingVersion.id,
          data,
        });
        toast.success('Versão atualizada com sucesso!');
      } else {
        await createVersionMutation.mutateAsync(data);
        toast.success('Versão criada com sucesso!');
      }
      handleCloseVersionDialog();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Erro ao salvar versão');
    }
  };

  const handleDeleteVersion = async (id: string) => {
    try {
      await deleteVersionMutation.mutateAsync(id);
      toast.success('Versão excluída com sucesso!');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Erro ao excluir versão');
    }
  };

  const handleModelFilterChange = (modelId: string | undefined) => {
    setSelectedModelFilter(modelId);
    resetVersionsPage();
  };

  return {
    // Models
    models,
    allModels,
    isLoadingModels,
    modelsPaginationMeta,
    setModelsPage,
    isModelDialogOpen,
    editingModel,
    handleOpenModelDialog,
    handleCloseModelDialog,
    handleSubmitModel,
    handleDeleteModel,

    // Versions
    versions,
    isLoadingVersions,
    versionsPaginationMeta,
    setVersionsPage,
    selectedModelFilter,
    setSelectedModelFilter: handleModelFilterChange,
    isVersionDialogOpen,
    editingVersion,
    handleOpenVersionDialog,
    handleCloseVersionDialog,
    handleSubmitVersion,
    handleDeleteVersion,
  };
}
